/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AccidentNotification } from '../models/AccidentNotification';
import type { AccidentNotificationCreate } from '../models/AccidentNotificationCreate';
import type { PaginatedAccidentNotificationListList } from '../models/PaginatedAccidentNotificationListList';
import type { PaginatedHistoryEntryList } from '../models/PaginatedHistoryEntryList';
import type { PatchedAccidentNotificationUpdate } from '../models/PatchedAccidentNotificationUpdate';
import type { PolicyLookupRequest } from '../models/PolicyLookupRequest';
import type { PolicyLookupResponse } from '../models/PolicyLookupResponse';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class ClaimsNotificationsService {

  /**
   * List accident notifications
   * /claims/notifications/
 *
 * List, create, retrieve, patch accident notifications, and RCA policy lookup.
   * @param accidentDateFrom 
   * @param accidentDateTo 
   * @param createdFrom 
   * @param createdTo 
   * @param damageTypes * `1` - Prejudicii cauzate autovehiculului
 * * `2` - Prejudicii cauzate altor bunuri
 * * `3` - Vătămări corporale, deces
   * @param insuredName 
   * @param notificationId 
   * @param ordering Ordering fields: created, accident_date, notification_id
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param policyNumber 
   * @param search Search fields: notification_id, vehicle_registration, policy_number, insured_name, parties__person__as_person__email, parties__person__as_person__first_name, parties__person__as_person__last_name, parties__person__as_person__identification_number, parties__person__as_legalentity__identification_number, parties__person__as_legalentity__name
   * @param status * `active` - Activ
 * * `expired` - Expirat
 * * `closed` - Închisă
   * @param vehicleRegistration 
   * @returns PaginatedAccidentNotificationListList 
   * @throws ApiError
   */
  public static claimsNotificationsList(
accidentDateFrom?: string,
accidentDateTo?: string,
createdFrom?: string,
createdTo?: string,
damageTypes?: Array<1 | 2 | 3>,
insuredName?: string,
notificationId?: string,
ordering?: string,
page?: number,
pageSize?: number,
policyNumber?: string,
search?: string,
status?: 'active' | 'closed' | 'expired',
vehicleRegistration?: string,
): CancelablePromise<PaginatedAccidentNotificationListList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/notifications/',
      query: {
        'accident_date_from': accidentDateFrom,
        'accident_date_to': accidentDateTo,
        'created_from': createdFrom,
        'created_to': createdTo,
        'damage_types': damageTypes,
        'insured_name': insuredName,
        'notification_id': notificationId,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'policy_number': policyNumber,
        'search': search,
        'status': status,
        'vehicle_registration': vehicleRegistration,
      },
    });
  }

  /**
   * Create a new accident notification
   * /claims/notifications/
 *
 * List, create, retrieve, patch accident notifications, and RCA policy lookup.
   * @param requestBody 
   * @returns AccidentNotification 
   * @throws ApiError
   */
  public static claimsNotificationsCreate(
requestBody: AccidentNotificationCreate,
): CancelablePromise<AccidentNotification> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/claims/notifications/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * Get accident notification detail
   * /claims/notifications/
 *
 * List, create, retrieve, patch accident notifications, and RCA policy lookup.
   * @param id A unique integer value identifying this Notificare accident RCAI.
   * @returns AccidentNotification 
   * @throws ApiError
   */
  public static claimsNotificationsRetrieve(
id: number,
): CancelablePromise<AccidentNotification> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/notifications/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * Update accident details (date, location, circumstances)
   * /claims/notifications/
 *
 * List, create, retrieve, patch accident notifications, and RCA policy lookup.
   * @param id A unique integer value identifying this Notificare accident RCAI.
   * @param requestBody 
   * @returns AccidentNotification 
   * @throws ApiError
   */
  public static claimsNotificationsPartialUpdate(
id: number,
requestBody?: PatchedAccidentNotificationUpdate,
): CancelablePromise<AccidentNotification> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/claims/notifications/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * Get full audit history for an accident notification
   * Returns all events related to this notification sorted by timestamp (default: newest first). Primary source: AuditLog (PostgreSQL, domain.entity.action events). Falls back to ApplicationLog (MongoDB) for pre-migration notifications. Always includes 1C sync events. Supports filtering by event_type, actor_type, date range.
   * @param id A unique integer value identifying this Notificare accident RCAI.
   * @param accidentDateFrom 
   * @param accidentDateTo 
   * @param actorType Filter by actor type
   * @param createdFrom 
   * @param createdTo 
   * @param damageTypes * `1` - Prejudicii cauzate autovehiculului
 * * `2` - Prejudicii cauzate altor bunuri
 * * `3` - Vătămări corporale, deces
   * @param dateFrom Filter events from this date (ISO 8601, e.g. 2026-03-01)
   * @param dateTo Filter events up to this date (ISO 8601, e.g. 2026-03-31)
   * @param eventType Filter by event type, e.g. claims.notification.create
   * @param insuredName 
   * @param notificationId 
   * @param ordering Sort order: 'timestamp' (asc) or '-timestamp' (desc, default)
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param policyNumber 
   * @param search Search fields: notification_id, vehicle_registration, policy_number, insured_name, parties__person__as_person__email, parties__person__as_person__first_name, parties__person__as_person__last_name, parties__person__as_person__identification_number, parties__person__as_legalentity__identification_number, parties__person__as_legalentity__name
   * @param status * `active` - Activ
 * * `expired` - Expirat
 * * `closed` - Închisă
   * @param vehicleRegistration 
   * @returns PaginatedHistoryEntryList 
   * @throws ApiError
   */
  public static claimsNotificationsHistoryList(
id: number,
accidentDateFrom?: string,
accidentDateTo?: string,
actorType?: 'api' | 'broker_user' | 'system' | 'user',
createdFrom?: string,
createdTo?: string,
damageTypes?: Array<1 | 2 | 3>,
dateFrom?: string,
dateTo?: string,
eventType?: string,
insuredName?: string,
notificationId?: string,
ordering?: '-timestamp' | 'timestamp',
page?: number,
pageSize?: number,
policyNumber?: string,
search?: string,
status?: 'active' | 'closed' | 'expired',
vehicleRegistration?: string,
): CancelablePromise<PaginatedHistoryEntryList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/notifications/{id}/history/',
      path: {
        'id': id,
      },
      query: {
        'accident_date_from': accidentDateFrom,
        'accident_date_to': accidentDateTo,
        'actor_type': actorType,
        'created_from': createdFrom,
        'created_to': createdTo,
        'damage_types': damageTypes,
        'date_from': dateFrom,
        'date_to': dateTo,
        'event_type': eventType,
        'insured_name': insuredName,
        'notification_id': notificationId,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'policy_number': policyNumber,
        'search': search,
        'status': status,
        'vehicle_registration': vehicleRegistration,
      },
    });
  }

  /**
   * Lookup RCA policy by registration number or policy ID
   * Search for an RCA policy using a single `search_value` field. If the value starts with `RCAI-` it is treated as a policy ID (fetched from SIA Asig API when not found locally). Otherwise it is treated as a vehicle registration number.
   * @param requestBody 
   * @returns PolicyLookupResponse 
   * @throws ApiError
   */
  public static claimsNotificationsLookupCreate(
requestBody: PolicyLookupRequest,
): CancelablePromise<PolicyLookupResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/claims/notifications/lookup/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

}
